import React, { useState, memo, useCallback, useMemo } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import {
  PostCardContainer,
  PostMediaSection,
  PostOverlay,
  PostBadge,
  PostCarouselMedia,
  useCarouselTapNav,
} from "@/components/PostCard";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { VideoPlayer } from "@/components/ui/VideoPlayer";
import { ErrorIcon } from "@/components/icons";
import { usePostCardAuthOptional } from "./contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";
import { PublicBadge } from "@/components/MediaCard/MediaBadges";

interface PostCardProps {
  post: Post;
  viewMode?: "grid" | "feed";
  isFocused?: boolean;
  overlayTopLeft?: React.ReactNode;
  overlayTopRight?: React.ReactNode;
  children?: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Extra classes on the card container (e.g. `border-0` inside panels). */
  className?: string;
}

/**
 * Slim core PostCard: media display layer only.
 *
 * Handles media conversion with auth context, carousel cycling,
 * reel video detection, standard badges, and video player modal.
 *
 * All admin logic, captions, status controls, and modals live in
 * composed card variants (EditorPostCard, ListPostCard, etc.).
 */
export const PostCard: React.FC<PostCardProps> = memo(
  ({
    post,
    viewMode = "grid",
    isFocused = false,
    overlayTopLeft,
    overlayTopRight,
    children,
    onMouseEnter,
    onMouseLeave,
    className,
  }) => {
    const feedMode = viewMode === "feed";
    const { selectedClient, realtime } = useAppStore();

    const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
    const [selectedVideoForPlayer, setSelectedVideoForPlayer] =
      useState<MediaItem | null>(null);

    const authContext = usePostCardAuthOptional();

    // Subscribe to publish progress for this post
    const publishProgress = realtime.publishProgress[post.id];
    const isPublishing =
      publishProgress &&
      ["preparing", "prepared", "publishing"].includes(publishProgress.status);
    // 'error' stays in the store (not auto-cleared) so the failure remains
    // visible on the card, not just in a transient toast.
    const isPublishFailed = publishProgress?.status === "error";

    const isPublishedStatus =
      post.status === PostStatus.PUBLISHED ||
      post.status === PostStatus.PARTIALLY_PUBLISHED;

    // Convert plain objects to MediaItem instances using auth context, then sort
    const sortedMediaItems = useMemo(() => {
      if (!post?.mediaItems || post.mediaItems.length === 0) return [];

      let userRole = authContext?.userRole || UserRole.ADMIN;
      let clientId = authContext?.clientId || selectedClient?.id;
      let authToken = authContext?.authToken;

      if (!authContext && userRole === UserRole.ADMIN) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            authToken = user.accessToken || undefined;
          } catch {
            authToken = undefined;
          }
        }
      }

      if (!clientId) {
        console.warn("PostCard: No clientId available for MediaItem conversion");
        return [];
      }

      if (userRole === UserRole.CLIENT && (!authToken || authToken.trim() === "")) {
        console.warn("PostCard: Client role requires authToken");
        return [];
      }

      const instances = post.mediaItems.map((item) => {
        const backendData =
          item instanceof MediaItem ? item.backendData : item;
        return MediaItem.fromBackend(backendData, userRole, clientId!, authToken);
      });

      return instances.sort((a, b) => {
        const posA = a.position ?? 999;
        const posB = b.position ?? 999;
        return posA - posB;
      });
    }, [post?.mediaItems, authContext, selectedClient?.id]);

    const imageCount = sortedMediaItems?.length || 0;
    const { index: currentImageIndex, goPrev, goNext } =
      useCarouselTapNav(imageCount);

    // Custom stream URL function for authentication
    const getStreamUrlByFileId = useCallback(
      (fileId: string): string | undefined => {
        const currentClientId = authContext?.clientId || selectedClient?.id;
        if (!currentClientId) return undefined;

        const mediaItem = sortedMediaItems.find(
          (item) =>
            item.fileIdentity === fileId,
        );
        if (!mediaItem) return undefined;

        return mediaItem.getStreamUrlWithQueryAuth();
      },
      [authContext?.clientId, selectedClient?.id, sortedMediaItems],
    );

    const handleVideoClick = useCallback((video: MediaItem) => {
      setSelectedVideoForPlayer(video);
      setIsVideoPlayerOpen(true);
    }, []);

    const handleVideoPlayerClose = useCallback(() => {
      setIsVideoPlayerOpen(false);
      setSelectedVideoForPlayer(null);
    }, []);

    // Helper to get the video MediaItem for reels
    const getReelVideoItem = useCallback(() => {
      if (post.postType !== "reel") return null;
      return sortedMediaItems.find((item) => item.isVideo()) || null;
    }, [post.postType, sortedMediaItems]);

    // Get current display image (thumbnail for reels, carousel for carousels)
    const getCurrentImage = useCallback(() => {
      if (post.postType === "reel") {
        if (post.thumbnailMediaItem) return post.thumbnailMediaItem;
        if (post.thumbnailMediaId && sortedMediaItems) {
          const thumbnailId =
            typeof post.thumbnailMediaId === "string"
              ? parseInt(post.thumbnailMediaId)
              : post.thumbnailMediaId;
          const thumbnailItem = sortedMediaItems.find(
            (item) => item.id === thumbnailId,
          );
          if (thumbnailItem && thumbnailItem.isImage()) return thumbnailItem;
        }
        if (sortedMediaItems) {
          const imageItem = sortedMediaItems.find((item) => item.isImage());
          if (imageItem) return imageItem;
        }
        return getReelVideoItem();
      }

      if (!sortedMediaItems || sortedMediaItems.length === 0) return null;
      if (post.isCarousel && sortedMediaItems.length > 1) {
        const imageIndex = Math.min(
          currentImageIndex,
          sortedMediaItems.length - 1,
        );
        return sortedMediaItems[imageIndex];
      }
      return sortedMediaItems[0];
    }, [
      sortedMediaItems,
      post.isCarousel,
      post.postType,
      post.thumbnailMediaItem,
      currentImageIndex,
      getReelVideoItem,
    ]);

    const currentImage = getCurrentImage();

    // For feed mode reels that are focused, show the video instead of thumbnail
    const displayMediaItem = useMemo(() => {
      if (feedMode && isFocused && post.postType === "reel") {
        const videoItem = getReelVideoItem();
        if (videoItem) return videoItem;
      }
      return currentImage;
    }, [feedMode, isFocused, post.postType, getReelVideoItem, currentImage]);

    // Both feed and grid carousels (non-reel, >1 item) get the Instagram-style
    // tap zones. The feed autoplays the focused video slide; the grid plays
    // lazily on tap — a wide grid must not autoplay dozens of videos at once.
    const isMultiCarousel =
      post.isCarousel && post.postType !== "reel" && imageCount > 1;
    const currentIsVideo = !!displayMediaItem?.isVideo();
    const isCarouselVideoSlide = feedMode
      ? isFocused && currentIsVideo
      : currentIsVideo;
    const lazyCarouselVideo = !feedMode;

    const mediaOverlays = (
      <>
        {/* Carousel indicator dots */}
        {post.postType !== "reel" &&
          post.isCarousel &&
          imageCount > 1 && (
            <PostOverlay.BottomRight className="pointer-events-none">
              <PostBadge.CarouselIndicator
                currentIndex={currentImageIndex}
                totalItems={imageCount}
              />
            </PostOverlay.BottomRight>
          )}

        {/* Bottom-left badges */}
        {(post.postType === "reel" ||
              post.status === PostStatus.AI_GENERATED ||
              post.publicStatus === "public" ||
              post.status === PostStatus.SCHEDULED ||
              isPublishFailed ||
              isPublishedStatus) && (
              <PostOverlay.BottomLeft className="flex gap-1.5">
                {post.postType === "reel" && <PostBadge.ReelBadge />}
                {post.status === PostStatus.AI_GENERATED && (
                  <PostBadge.AIGeneratedBadge />
                )}
                {post.publicStatus === "public" && <PublicBadge />}
                {post.status === PostStatus.SCHEDULED && (
                  <PostBadge.ScheduledBadge scheduledAt={post.scheduledAt} />
                )}
                {isPublishedStatus && (
                  <PostBadge.ScheduledBadge
                    scheduledAt={post.publishedAt}
                    variant="published"
                  />
                )}
                {isPublishFailed && (
                  <div
                    className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded-full"
                    title={publishProgress?.error || "Publishing failed"}
                  >
                    <ErrorIcon className="w-3 h-3" />
                    Publish failed
                  </div>
                )}
              </PostOverlay.BottomLeft>
            )}

            {/* Publishing indicator */}
            {isPublishing && (
              <PostOverlay.TopRight>
                <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                  <svg
                    className="w-3 h-3 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Publishing
                </div>
              </PostOverlay.TopRight>
            )}

        {/* Composable overlay slots */}
        {overlayTopLeft}
        {!isPublishing && overlayTopRight}
      </>
    );

    return (
      <PostCardContainer
        viewMode={viewMode}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Media section */}
        {displayMediaItem ? (
          isMultiCarousel ? (
            <PostCarouselMedia
              currentItem={displayMediaItem}
              isVideoSlide={isCarouselVideoSlide}
              showTapZones
              onPrev={goPrev}
              onNext={goNext}
              getStreamUrlByFileId={getStreamUrlByFileId}
              lazyVideo={lazyCarouselVideo}
              className={feedMode ? undefined : "rounded-t-xl"}
            >
              {mediaOverlays}
            </PostCarouselMedia>
          ) : (
            <PostMediaSection
              viewMode={feedMode ? "feed" : "grid"}
              mediaItem={displayMediaItem}
              isReel={post.postType === "reel"}
              isCarousel={post.isCarousel}
              autoPlay={feedMode && isFocused && post.postType === "reel"}
              onClick={(e) => {
                // Carousels (grid): forward-cycle on tap.
                if (
                  post.isCarousel &&
                  post.postType !== "reel" &&
                  sortedMediaItems &&
                  sortedMediaItems.length > 1
                ) {
                  e.stopPropagation();
                  e.preventDefault();
                  goNext();
                  return;
                }

                // Reels in feed mode: no click (video plays inline)
                if (feedMode && post.postType === "reel") return;

                // Reels and stories in non-feed mode: open video player
                if (post.postType === "reel" || post.postType === "story") {
                  e.stopPropagation();
                  const videoItem =
                    post.postType === "reel"
                      ? getReelVideoItem()
                      : sortedMediaItems.find((item) => item.isVideo()) || null;
                  if (videoItem) handleVideoClick(videoItem);
                }
              }}
            >
              {mediaOverlays}
            </PostMediaSection>
          )
        ) : (
          <div className="relative overflow-hidden rounded-t-xl">
            <div className="aspect-4/5 bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm text-gray-500">No images</span>
              </div>
            </div>
          </div>
        )}

        {/* Children slot (content section from composed cards) */}
        {children}

        {/* Video Player modal */}
        {selectedVideoForPlayer && (
          <VideoPlayer
            mediaItem={selectedVideoForPlayer}
            isOpen={isVideoPlayerOpen}
            onClose={handleVideoPlayerClose}
            getStreamUrlByFileId={getStreamUrlByFileId}
            clientId={authContext?.clientId || selectedClient?.id}
          />
        )}
      </PostCardContainer>
    );
  },
);

PostCard.displayName = "PostCard";
export default PostCard;
